import { inject, Injectable } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import {
  AnonymizePatientGQL,
  AnonymizePatientMutation,
  CreatePatientGQL,
  CreatePatientMutation,
  GetPatientByIdGQL,
  GetPatientByIdQuery,
  GetPatientByOrderIdGQL,
  GetPatientByOrderIdQuery,
  GetPatientsCountByTenantIdGQL,
  GetPatientsCountByTenantIdQuery,
  ListPatientsBySearchTermsGQL,
  ListPatientsBySearchTermsQuery,
  ListPatientsByTenantIdGQL,
  ListPatientsByTenantIdQuery,
  Patient,
  UpdatePatientGQL,
  UpdatePatientMutation,
} from '@insolution/graphql-data-access';
import { CurrentTenantService } from '@insolution/tenant-data-access';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PatientService {
  private currentTenantService = inject(CurrentTenantService);
  private listPatientsByTenantIdGQL = inject(ListPatientsByTenantIdGQL);
  private listPatientsBySearchTermsGQL = inject(ListPatientsBySearchTermsGQL);
  private anonymizePatientGQL = inject(AnonymizePatientGQL);
  private createPatientGQL = inject(CreatePatientGQL);
  private updatePatientGQL = inject(UpdatePatientGQL);
  private getPatientByIdGQL = inject(GetPatientByIdGQL);
  private getPatientByOrderIdGQL = inject(GetPatientByOrderIdGQL);
  private getPatientsCountByTenantIdGQL = inject(GetPatientsCountByTenantIdGQL);

  public watchPatientsByTenantId$(tenantId: string): Observable<ListPatientsByTenantIdQuery['listPatientsByTenantId']> {
    return this.listPatientsByTenantIdGQL
      .watch({ tenant_id: tenantId })
      .valueChanges.pipe(
        map((result) => (result.data.listPatientsByTenantId ?? []).filter((patient) => patient !== null))
      );
  }

  public async getFilteredPatients(
    searchTerms: string
  ): Promise<ListPatientsByTenantIdQuery['listPatientsByTenantId']> {
    const tenantId = this.currentTenantService.currentTenantId();

    if (!tenantId) {
      throw new Error('No tenant selected');
    }

    const items = [];
    let nextToken: string | null = null;

    do {
      const result: ApolloQueryResult<ListPatientsBySearchTermsQuery> = await firstValueFrom(
        this.listPatientsBySearchTermsGQL.fetch({
          tenant_id: tenantId,
          search_terms: searchTerms,
          nextToken,
          limit: 200,
        })
      );
      items.push(...(result.data.listPatientsBySearchTerms?.items ?? []));
      nextToken = result.data.listPatientsBySearchTerms?.nextToken ?? null;
    } while (nextToken);

    return items;
  }

  public getPatientById(id: string): Promise<GetPatientByIdQuery['getPatientById']> {
    return firstValueFrom(this.getPatientByIdGQL.fetch({ id })).then((result) => result.data.getPatientById!);
  }

  public getPatientByOrderId(orderId: string): Promise<GetPatientByOrderIdQuery['getPatientByOrderId']> {
    return firstValueFrom(this.getPatientByOrderIdGQL.fetch({ id: orderId })).then(
      (result) => result.data.getPatientByOrderId
    );
  }

  public async getTotalNumberOfPatients(): Promise<number> {
    const tenantId = this.currentTenantService.currentTenantId();

    if (!tenantId) {
      throw new Error('No tenant selected');
    }

    try {
      let totalCount = 0;
      let nextToken: string | null = null;

      do {
        const result: ApolloQueryResult<GetPatientsCountByTenantIdQuery> = await firstValueFrom(
          this.getPatientsCountByTenantIdGQL.fetch({
            tenant_id: tenantId,
            nextToken: nextToken ?? undefined,
          })
        );

        totalCount += result.data.getPatientsCountByTenantId?.count ?? 0;
        nextToken = result.data.getPatientsCountByTenantId?.nextToken ?? null;
      } while (nextToken);

      return totalCount;
    } catch (error) {
      console.error('Error getting total patients:', error);
      return Promise.reject(error);
    }
  }

  public async createPatient(patient: Patient): Promise<CreatePatientMutation['createPatient']> {
    const tenantRef = this.currentTenantService.currentTenantRef();
    const tenantId = this.currentTenantService.currentTenantId();

    if (!tenantRef || !tenantId) {
      throw new Error('No tenant selected');
    }

    try {
      const createPatientResult = await firstValueFrom(
        this.createPatientGQL.mutate(
          {
            input: {
              ...patient,
              tenant_id: tenantId,
              tenant_ref: tenantRef,
            },
          },
          {
            update: (cache) => {
              cache.modify({
                fields: {
                  listPatientsBySearchTerms(existing, { DELETE }) {
                    return DELETE;
                  },
                  listPatientsByTenantId(existing, { DELETE }) {
                    return DELETE;
                  },
                  getPatientsCountByTenantId(existing, { DELETE }) {
                    return DELETE;
                  },
                },
              });
            },
          }
        )
      );

      if (createPatientResult.errors?.length) {
        throw new Error(createPatientResult.errors[0].message || 'Creating patient failed');
      }

      return createPatientResult.data?.createPatient;
    } catch (error) {
      console.error(`Error creating patient: `, error);
      return Promise.reject(error);
    }
  }

  public async updatePatient(patient: Patient): Promise<UpdatePatientMutation['updatePatient']> {
    try {
      const updatePatientResult = await firstValueFrom(
        this.updatePatientGQL.mutate(
          { input: patient },
          {
            update: (cache) => {
              cache.modify({
                fields: {
                  listPatientsBySearchTerms(existing, { DELETE }) {
                    return DELETE;
                  },
                  listPatientsByTenantId(existing, { DELETE }) {
                    return DELETE;
                  },
                  getPatientById(existing, { DELETE }) {
                    return DELETE;
                  },
                },
              });
            },
          }
        )
      );

      if (updatePatientResult.errors?.length) {
        throw new Error(updatePatientResult.errors[0].message || 'Updating patient failed');
      }

      return updatePatientResult.data?.updatePatient;
    } catch (error) {
      console.error(`Error updating patient: `, error);
      return Promise.reject(error);
    }
  }

  public async anonymizePatient(patient: {
    id: Patient['id'];
    tenant_id: Patient['id'];
  }): Promise<AnonymizePatientMutation['anonymizePatient']> {
    try {
      const anonymizePatientResult = await firstValueFrom(
        this.anonymizePatientGQL.mutate(
          { input: { id: patient.id } },
          {
            update: (cache) => {
              cache.modify({
                fields: {
                  listPatientsByTenantId(existing, { DELETE }) {
                    return DELETE;
                  },
                  getPatientById(existing, { DELETE }) {
                    return DELETE;
                  },
                },
              });
            },
          }
        )
      );

      if (anonymizePatientResult.errors?.length) {
        throw new Error(anonymizePatientResult.errors[0].message || 'Updating patient failed');
      }

      return anonymizePatientResult.data?.anonymizePatient;
    } catch (error) {
      console.error(`Error updating patient: `, error);
      return Promise.reject(error);
    }
  }
}
